import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  listUserGroupedAlerts,
  listUserAlertOccurrences,
  listAlertOccurrences,
  resolveAlertRef,
  resolveAccountAlertRef,
} from '@kirin/api-client/api/alerts';
import type {
  AlertsQuery,
  AlertOccurrencesQuery,
} from '@kirin/api-client/contracts/alerts';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { apiClient } from '@/lib/api-client';
import { toggleSortOrder } from '@/components/shared/sortUtils';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

export interface UseGroupedAlertsTabStateResult<T extends string> {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  sortBy: T;
  sortOrder: SortOrder;
  handleSort: (field: T) => void;
}

/**
 * Shared state and sort logic for the grouped alerts tab.
 * Use with useUserGroupedAlerts and pass page, limit, sortBy, sortOrder
 * into the query params.
 */
export function useGroupedAlertsTabState<T extends string>(
  defaultSortField: T
): UseGroupedAlertsTabStateResult<T> {
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<T>(defaultSortField);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Desc);

  const handleSort = useCallback(
    (field: T): void => {
      if (field === sortBy) {
        setSortOrder(prev => toggleSortOrder(prev));
      } else {
        setSortBy(field);
        setSortOrder(SortOrder.Desc);
      }
      setPage(1);
    },
    [sortBy]
  );

  return { page, setPage, sortBy, sortOrder, handleSort };
}

/**
 * Hook to fetch grouped alerts for the authenticated user.
 *
 * @deprecated The server now returns the table-engine shape for GET /account/alerts.
 * Use the data table hooks in DataTableUserAlertsTab/api.ts instead, which call
 * {@link listUserGroupedDataTableAlerts} and parse the response correctly.
 *
 * @param params - Pagination and sorting parameters
 * @param enabled - Whether to enable the query (default: true)
 */
export function useUserGroupedAlerts(
  params: Partial<AlertsQuery> = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.alerts.list(params),
    queryFn: () => listUserGroupedAlerts(apiClient, params),
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch individual occurrences for a specific user grouped alert.
 *
 * @param alertKey - The alert key to fetch occurrences for
 * @param params - Pagination parameters
 * @param enabled - Whether to enable the query (default: true when alertKey is provided)
 */
export function useUserAlertOccurrences(
  alertKey: string | undefined,
  params: Partial<AlertOccurrencesQuery> = {},
  enabled: boolean = true
) {
  const isEnabled = enabled && !!alertKey;

  return useQuery({
    queryKey: queryKeys.alerts.occurrences(alertKey ?? '', params),
    queryFn: () => {
      if (!alertKey) {
        throw new Error('alertKey is required');
      }
      return listUserAlertOccurrences(apiClient, alertKey, params);
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * Hook to fetch individual occurrences for a specific organization grouped alert.
 *
 * @param organizationId - Organization ID (required)
 * @param alertKey - The alert key to fetch occurrences for
 * @param params - Pagination parameters
 * @param enabled - Whether to enable the query
 */
export function useOrganizationAlertOccurrences(
  organizationId: string | undefined,
  alertKey: string | undefined,
  params: Partial<AlertOccurrencesQuery> = {},
  enabled: boolean = true
) {
  const isEnabled = enabled && !!organizationId && !!alertKey;

  return useQuery({
    queryKey: queryKeys.organizations.alertOccurrences(
      organizationId ?? '',
      alertKey ?? '',
      params
    ),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      if (!alertKey) {
        throw new Error('alertKey is required');
      }
      return listAlertOccurrences(apiClient, organizationId, alertKey, params);
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * KIR-2788: resolve one notification token to the exact alert(s) it pointed at.
 * The token is a single 64-hex value — a finding_key (one alert) or an alert-set
 * id (composite / secret) — and the backend resolves either. A 404 (unknown /
 * expired / wrong-scope token) throws so the landing view degrades to the list.
 *
 * @param organizationId - Organization ID (required)
 * @param token - The notification token from the deep link
 * @param enabled - Whether to enable the query
 */
export function useOrganizationAlertRef(
  organizationId: string | undefined,
  token: string | undefined,
  enabled: boolean = true
) {
  const isEnabled = enabled && !!organizationId && !!token;

  return useQuery({
    queryKey: queryKeys.organizations.alertRef(
      organizationId ?? '',
      token ?? ''
    ),
    queryFn: ({ signal }) => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      if (!token) {
        throw new Error('token is required');
      }
      return resolveAlertRef(apiClient, organizationId, token, { signal });
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}

/**
 * KIR-2788: personal (account) variant of {@link useOrganizationAlertRef} —
 * resolves a personal-account user's notification token (`/dashboard/alerts/
 * {token}`) to the exact alert(s), scoped to the account owner.
 *
 * @param token - The notification token from the deep link
 * @param enabled - Whether to enable the query
 */
export function useAccountAlertRef(
  token: string | undefined,
  enabled: boolean = true
) {
  const isEnabled = enabled && !!token;

  return useQuery({
    queryKey: queryKeys.account.alertRef(token ?? ''),
    queryFn: ({ signal }) => {
      if (!token) {
        throw new Error('token is required');
      }
      return resolveAccountAlertRef(apiClient, token, { signal });
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
  });
}
